import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { institutionApi } from '../api/institution.api'
import { useInstitutionClasses, useProgressReport } from '../hooks/useInstitution'
import { ReportFiltersBar } from './ReportFiltersBar'
import type { ReportFilters } from '../types'

/** Rapports de progression et leurs exports (spécifications § 9.4). */
export function ProgressReportPanel() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<ReportFilters>({})
  const { data: report, isLoading, isError } = useProgressReport(filters)
  const { data: classes } = useInstitutionClasses()
  const [exportError, setExportError] = useState(false)

  async function handleExport(format: 'csv' | 'html') {
    setExportError(false)

    try {
      const response = await institutionApi.downloadReport(filters, format)
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `rapport-progression.${format}`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      setExportError(true)
    }
  }

  return (
    <section aria-labelledby="report-title" className="flex flex-col gap-4">
      <h2 id="report-title" className="font-display text-lg font-semibold text-on-surface">
        {t('institution.report.title')}
      </h2>

      <ReportFiltersBar filters={filters} classes={classes ?? []} onChange={setFilters} />

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={() => handleExport('csv')}>
          {t('institution.report.export_csv')}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => handleExport('html')}>
          {t('institution.report.export_print')}
        </Button>
      </div>

      {exportError && <Alert variant="error">{t('institution.report.export_error')}</Alert>}
      {isError && <Alert variant="error">{t('institution.report.load_error')}</Alert>}
      {isLoading && <div className="h-40 animate-pulse rounded-2xl bg-surface-container" />}

      {report && (
        <>
          <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {(
              [
                ['students', report.summary.students],
                ['active_students', report.summary.active_students],
                ['sessions', report.summary.sessions],
                ['concepts', report.summary.concepts_mastered],
              ] as const
            ).map(([key, value]) => (
              <div key={key} className="rounded-2xl bg-surface-low px-4 py-3">
                <dt className="text-xs font-medium text-on-surface-variant">
                  {t(`institution.report.metrics.${key}`)}
                </dt>
                <dd className="font-display text-xl font-bold tabular-nums text-on-surface">
                  {value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <caption className="sr-only">{t('institution.report.title')}</caption>
              <thead>
                <tr className="border-b border-outline-variant text-left text-on-surface-variant">
                  <th scope="col" className="py-2 pr-3 font-medium">
                    {t('institution.report.columns.name')}
                  </th>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    {t('institution.report.columns.classes')}
                  </th>
                  <th scope="col" className="py-2 pr-3 text-right font-medium">
                    {t('institution.report.columns.sessions')}
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    {t('institution.report.columns.concepts')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.students.map((row) => (
                  <tr key={row.user_id} className="border-b border-outline-variant/50">
                    <td className="py-3 pr-3 font-medium text-on-surface">{row.name}</td>
                    <td className="py-3 pr-3 text-on-surface-variant">
                      {row.class_names.join(', ')}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums">{row.sessions}</td>
                    <td className="py-3 text-right tabular-nums">{row.concepts_mastered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
